import React from "react";

import { Column } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { FiLink2 } from "react-icons/fi";

import { Customer } from "../../../models/customer";
import { PurchaseOrder } from "../../../models/purchase.order";
import { Supplier } from "../../../models/supplier";
import { formatDateWithoutTimeZone } from "../../../util/Format";
import { PurchaseOrderSearch } from "../../Generic/ShipmentOrders/types";
import BadgePOStatus from "../../Orders/StatusBadge";
import { BookingOrderReferenceRow, BookingOrderStatusBadge } from "../Maintenance/Orders/styles";
import { BookingOrderStatuses } from "../Maintenance/Orders/types";

export const usePurchaseOrderColumns = (showOrderStatus?: boolean) => {
  const { t: translate } = useTranslation();

  const renderReferenceFromIntegration = (reference: string, order: PurchaseOrder) => {
    if (!order.fromIntegration) return reference;

    return (
      <BookingOrderReferenceRow>
        <span>{reference}</span>
        <FiLink2 color="#38CFC4" size={15} />
      </BookingOrderReferenceRow>
    );
  };

  const baseColumns: Column[] = [
    {
      name: "reference",
      title: `${translate("order")}#`,
      formatComponent: renderReferenceFromIntegration,
      hideSort: true,
      searchIdentifier: "reference",
      style: {
        paddingLeft: "8px",
      },
    },
    {
      name: "type",
      title: translate("type"),
    },
    {
      name: "remark",
      title: translate("remark"),
      hideSort: true,
      searchIdentifier: "remark",
      style: {
        paddingRight: "8px",
      },
    },
    {
      name: "consignee",
      title: translate("consignee"),
      formatText: (consignee?: Customer) => consignee?.name || "",
      hideSort: true,
      searchIdentifier: PurchaseOrderSearch.CONSIGNEE_NAME,
      style: {
        minWidth: "115px",
      },
    },
    {
      name: "date",
      title: translate("orderDate"),
      formatText: (value?: string) => formatDateWithoutTimeZone(value),
      hideSort: true,
      hideSearch: true,
      style: {
        minWidth: "80px",
      },
    },
    {
      name: "supplier",
      title: translate("shipper"),
      formatText: (supplier?: Supplier) => supplier?.name || "",
      hideSort: true,
      searchIdentifier: PurchaseOrderSearch.SUPPLIER_NAME,
      style: {
        minWidth: "135px",
      },
    },
  ];

  const columns: Column[] = [
    ...baseColumns,
    {
      name: "orderedQuantity",
      title: translate("orderedQuantity"),
      hideSort: true,
      hideSearch: true,
      style: {
        minWidth: "70px",
      },
    },
    {
      name: "totalQuantity",
      title: translate("totalQuantity"),
      formatText: (text?: string) => text || "-",
      hideSort: true,
      hideSearch: true,
      style: {
        minWidth: "70px",
      },
    },
    {
      name: "percentage",
      title: translate("variance"),
      formatText: (value?: number) => (value ? `${value?.toFixed(2)}%` : "-"),
      hideSort: true,
      hideSearch: true,
      style: {
        paddingRight: "8px",
      },
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (value: string) => {
        if (!showOrderStatus) return <BookingOrderStatusBadge value={BookingOrderStatuses.ADDED} />;

        return <BadgePOStatus value={value} />;
      },
      hideSort: true,
      hideSearch: true,
    },
  ];

  return {
    baseColumns,
    columns,
  };
};
